import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';

import { trackSearchWithinResults } from '../../utils/tracking/searchBrowse/filterTracking.ts';
import { SbSharedRefineMenuFilters } from '../../finding/SbSharedRefineMenu/SbSharedRefineMenuFilters/SbSharedRefineMenuFilters.tsx';
import { SbSharedRefineMenuDisplayPrefsSelect } from '../../finding/SbSharedRefineMenu/SbSharedRefineMenuDisplayPrefsSelect/SbSharedRefineMenuDisplayPrefsSelect.jsx';
import { SbSharedRefineMenuSearchWithin } from '../../finding/SbSharedRefineMenu/SbSharedRefineMenuSearchWithin/SbSharedRefineMenuSearchWithin.tsx';
import { sbRespRefineMenuFiltersMap } from '../../finding/SbRespRefineMenu/SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap.ts';
import { SbRespStickyMenu } from '../../finding/SbRespStickyMenu/SbRespStickyMenu.jsx';
import {
    FILTER_REFINE_SEARCH,
    PRICE,
} from '../../finding/SbSharedRefineMenu/sbSharedRefineMenuConstants.ts';
import { CATEGORY_LEVEL_1 } from '../../utils/categoryHelpers.ts';
import { ATTRIBUTE_ITEMTYPE, ATTRIBUTE_JEWELRYTYPE } from '../../constants/attributesConstants.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';

import { type BuyRespRefineMenu_viewer$key } from './__generated__/BuyRespRefineMenu_viewer.graphql.ts';
import { type BuyRespRefineMenu_filters$key } from './__generated__/BuyRespRefineMenu_filters.graphql.ts';
import { type BuyRespRefineMenu_itemSearch$key } from './__generated__/BuyRespRefineMenu_itemSearch.graphql.ts';
import { type BuyRespRefineMenu_user$key } from './__generated__/BuyRespRefineMenu_user.graphql.ts';
import { useSbSelector } from '../../reducers/useSbSelector.ts';

const viewerFragment = graphql`
    fragment BuyRespRefineMenu_viewer on Viewer {
        ...SbSharedRefineMenuFilters_viewer
    }
`;
const itemSearchFragment = graphql`
    fragment BuyRespRefineMenu_itemSearch on ItemSearchQueryConnection {
        appliedFilters {
            name
            canBeDismissed
        }
        ...SbSharedRefineMenuFilters_itemSearch
        ...SbSharedRefineMenuDisplayPrefsSelect_itemSearch
        ...SbSharedRefineMenuSearchWithin_itemSearch
    }
`;
const filtersFragment = graphql`
    fragment BuyRespRefineMenu_filters on SearchBrowseFilter @relay(plural: true) {
        ...SbSharedRefineMenuFilters_filters
        ...SbSharedRefineMenuDisplayPrefsSelect_filters
    }
`;
const userFragment = graphql`
    fragment BuyRespRefineMenu_user on User {
        ...SbSharedRefineMenuFilters_user
        ...SbSharedRefineMenuDisplayPrefsSelect_user
    }
`;

type Props = {
    viewer: BuyRespRefineMenu_viewer$key;
    itemSearch: BuyRespRefineMenu_itemSearch$key;
    user: BuyRespRefineMenu_user$key;
    filters: BuyRespRefineMenu_filters$key;
};

export const BuyRespRefineMenu: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    filters: filtersRef,
    user: userRef,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const filters = useFragment(filtersFragment, filtersRef);
    const user = useFragment(userFragment, userRef);
    const { isRemoveCountsVariant } = useServerVarsContext();
    const afterDisplay = useSbSelector(state => state.relayVariables.variables.afterDisplay);
    const generatedUriRef = useSbSelector(state => state.filters.generatedUriRef) || undefined;
    const { appliedFilters } = itemSearch;
    const appliedSearchFilter = appliedFilters?.find(
        filter => filter?.name === FILTER_REFINE_SEARCH
    );
    const showSearchFilter = appliedSearchFilter ? appliedSearchFilter.canBeDismissed : true;
    const isCategoryL1Applied = appliedFilters?.some(filter => filter?.name === CATEGORY_LEVEL_1);

    const initiallyExpandedFilters = isCategoryL1Applied
        ? [CATEGORY_LEVEL_1, PRICE]
        : [CATEGORY_LEVEL_1, PRICE, ATTRIBUTE_ITEMTYPE, ATTRIBUTE_JEWELRYTYPE];

    return (
        <SbRespStickyMenu>
            {showSearchFilter && (
                <SbSharedRefineMenuSearchWithin
                    horizontalSpacing="small"
                    onSubmit={trackSearchWithinResults}
                    itemSearch={itemSearch}
                    hideResultsCount={isRemoveCountsVariant}
                />
            )}
            <SbSharedRefineMenuFilters
                user={user}
                uriRef={generatedUriRef}
                viewer={viewer}
                filters={filters}
                isClient={afterDisplay}
                itemSearch={itemSearch}
                filterMap={sbRespRefineMenuFiltersMap}
                initiallyExpanded={filterName => initiallyExpandedFilters.includes(filterName)}
            />
            <SbSharedRefineMenuDisplayPrefsSelect
                user={user}
                filters={filters}
                itemSearch={itemSearch}
                isClient={afterDisplay}
            />
        </SbRespStickyMenu>
    );
};
